import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import ProductCard from './popup/product-card';

import { ppHeadingOverridesOpen } from '../shared/utils';

import styles from './outfits.module.scss';

const OutfitCard = ({
  appearance = 'light',
  data,
  isActive,
  userSession,
  setIsPopupOpen,
  setIsSwiperLocked,
  purchaseIntent = false,
  productsData,
  isProductsLoading,
  isOverlayPinnedOpen,
  setIsOverlayPinnedOpen
}) => {
  const openOutfitsPopup = () => {
    setIsPopupOpen(true);
    window.scrollLock();
    ppHeadingOverridesOpen();
  };

  return (
    <div
      className={`${styles.slide} ${isActive ? styles.active : ''} ${
        purchaseIntent && isActive ? styles.purchaseIntent : ''
      } ${isOverlayPinnedOpen ? styles.overlayPinnedOpen : ''}`}
    >
      <div className={styles.blur} />
      <div className={`w-full h-full aspect-[34/40]`}>
        <img
          src={data.image}
          alt={data.alt_text}
          style={{
            aspectRatio: '485/590'
          }}
        />
      </div>
      <div
        className={`${styles.content} ${
          appearance === 'light'
            ? styles.lightModeContent
            : styles.darkModeContent
        }`}
      >
        <h3 className={styles.brand}>{data.large_text}</h3>
        {appearance === 'dark' && <div className={styles.divider} />}
        <span className={styles.title}>{data.small_text}</span>
        <ViewProducts openOutfitsPopup={openOutfitsPopup} />
      </div>
      {purchaseIntent && (
        <ProductsOverlay
          products={data?.products}
          userSession={userSession}
          openOutfitsPopup={openOutfitsPopup}
          isActive={isActive}
          setIsSwiperLocked={setIsSwiperLocked}
          productsData={productsData}
          isProductsLoading={isProductsLoading}
          onOverlayPinnedChange={setIsOverlayPinnedOpen}
        />
      )}
    </div>
  );
};

export const OutfitCardSkeleton = ({ isActive = false }) => {
  return (
    <div className={`${styles.slide} ${isActive ? styles.active : ''}`}>
      <div className={styles.blur} />
      <div
        className={`bg-gray-200 w-full h-full aspect-[34/40] ${
          !isActive ? 'animate-pulse' : ''
        }`}
      />
      <div className={styles.content} style={{ opacity: isActive ? 1 : 0 }} />
    </div>
  );
};

const ViewProducts = ({ openOutfitsPopup }) => (
  <button
    type="button"
    className={styles.viewProducts}
    data-outfits-view-products="true"
    onClick={openOutfitsPopup}
    aria-label="View outfit products"
  >
    <p>View Products</p>
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.2768 7.4643L8.3393 11.4018C8.21601 11.5251 8.0488 11.5943 7.87445 11.5943C7.7001 11.5943 7.53289 11.5251 7.40961 11.4018C7.28633 11.2785 7.21707 11.1113 7.21707 10.937C7.21707 10.7626 7.28633 10.5954 7.40961 10.4721L10.2266 7.65625H2.1875C2.01345 7.65625 1.84653 7.58711 1.72346 7.46404C1.60039 7.34097 1.53125 7.17405 1.53125 7C1.53125 6.82596 1.60039 6.65904 1.72346 6.53596C1.84653 6.41289 2.01345 6.34375 2.1875 6.34375H10.2266L7.4107 3.52625C7.28742 3.40297 7.21816 3.23576 7.21816 3.06141C7.21816 2.88706 7.28742 2.71985 7.4107 2.59657C7.53399 2.47328 7.7012 2.40402 7.87555 2.40402C8.0499 2.40402 8.21711 2.47328 8.34039 2.59657L12.2779 6.53407C12.3391 6.59512 12.3876 6.66765 12.4207 6.74751C12.4538 6.82737 12.4707 6.91297 12.4706 6.99941C12.4705 7.08585 12.4534 7.17141 12.4201 7.25119C12.3868 7.33097 12.3381 7.4034 12.2768 7.4643Z"
        fill="currentColor"
      />
    </svg>
  </button>
);

const ProductsOverlay = ({
  products,
  userSession,
  openOutfitsPopup,
  isActive,
  productsData,
  isProductsLoading,
  setIsSwiperLocked,
  onOverlayPinnedChange
}) => {
  const FADE_MS = 300;

  const [activeMarkerIndex, setActiveMarkerIndex] = useState(null);
  const [cardOffsets, setCardOffsets] = useState({});

  const containerRef = useRef(null);
  const cardRefs = useRef({});
  const closeTimerRef = useRef(null);

  const closeMarker = (index) => {
    setActiveMarkerIndex(null);
    setIsSwiperLocked(false);
    onOverlayPinnedChange?.(false);

    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setCardOffsets((prev) => ({ ...prev, [index]: 0 }));
    }, FADE_MS);
  };

  const handleMarkerClick = (index) => {
    if (activeMarkerIndex === index) {
      closeMarker(index);
      return;
    }

    window.clearTimeout(closeTimerRef.current);
    setActiveMarkerIndex(index);
    setIsSwiperLocked(true);
    onOverlayPinnedChange?.(true);
  };

  useEffect(() => {
    if (!isActive) {
      setActiveMarkerIndex(null);
      setCardOffsets({});
      onOverlayPinnedChange?.(false);
    }
  }, [isActive, onOverlayPinnedChange]);

  useEffect(() => {
    return () => setIsSwiperLocked(false);
  }, [setIsSwiperLocked]);

  useLayoutEffect(() => {
    if (activeMarkerIndex === null || !containerRef.current) return;

    const cardEl = cardRefs.current[activeMarkerIndex];
    if (!cardEl) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const cardRect = cardEl.getBoundingClientRect();
    const overflow = cardRect.bottom - containerRect.bottom;

    setCardOffsets((prev) => ({
      ...prev,
      [activeMarkerIndex]: overflow > 0 ? -overflow : 0
    }));
  }, [activeMarkerIndex]);

  if (!productsData && !isProductsLoading) return null;

  return (
    <div
      className={styles.productsOverlay}
      ref={containerRef}
      data-outfits-products-overlay="true"
    >
      {products.map((product, index) => {
        const markerKey =
          product?.prod_id || `${product?.x_pos}-${product?.y_pos}-${index}`;
        const markerLeft = parseFloat(product.x_pos);
        const openToLeft = Number.isFinite(markerLeft) && markerLeft < 50;
        const isOpen = activeMarkerIndex === index;
        return (
          <div
            key={markerKey}
            className={`${styles.markerContainer} ${
              isOpen ? styles.markerContainerOpen : ''
            }`}
            style={{
              top: `${product.y_pos}%`,
              left: `${product.x_pos}%`
            }}
          >
            <button
              type="button"
              className={styles.productMarker}
              onClick={() => handleMarkerClick(index)}
              aria-label={
                isOpen
                  ? 'Close product quick view marker'
                  : 'Open product quick view marker'
              }
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform:
                    activeMarkerIndex === index
                      ? 'rotate(135deg)'
                      : 'rotate(0deg)'
                }}
              >
                <path
                  d="M16 2.5C13.33 2.5 10.7199 3.29176 8.49981 4.77516C6.27974 6.25856 4.54942 8.36697 3.52763 10.8338C2.50585 13.3006 2.2385 16.015 2.7594 18.6337C3.28031 21.2525 4.56606 23.6579 6.45406 25.5459C8.34207 27.434 10.7475 28.7197 13.3663 29.2406C15.985 29.7615 18.6994 29.4942 21.1662 28.4724C23.633 27.4506 25.7414 25.7203 27.2248 23.5002C28.7082 21.2801 29.5 18.67 29.5 16C29.496 12.4208 28.0724 8.98932 25.5416 6.45844C23.0107 3.92756 19.5792 2.50397 16 2.5ZM16 26.5C13.9233 26.5 11.8932 25.8842 10.1665 24.7304C8.4398 23.5767 7.09399 21.9368 6.29927 20.0182C5.50455 18.0996 5.29662 15.9884 5.70176 13.9516C6.10691 11.9148 7.10693 10.0438 8.57538 8.57538C10.0438 7.10693 11.9148 6.1069 13.9516 5.70175C15.9884 5.29661 18.0996 5.50454 20.0182 6.29926C21.9368 7.09399 23.5767 8.4398 24.7304 10.1665C25.8842 11.8932 26.5 13.9233 26.5 16C26.497 18.7839 25.3898 21.4528 23.4213 23.4213C21.4529 25.3898 18.7839 26.497 16 26.5ZM22.5 16C22.5 16.3978 22.342 16.7794 22.0607 17.0607C21.7794 17.342 21.3978 17.5 21 17.5H17.5V21C17.5 21.3978 17.342 21.7794 17.0607 22.0607C16.7794 22.342 16.3978 22.5 16 22.5C15.6022 22.5 15.2206 22.342 14.9393 22.0607C14.658 21.7794 14.5 21.3978 14.5 21V17.5H11C10.6022 17.5 10.2207 17.342 9.93935 17.0607C9.65804 16.7794 9.50001 16.3978 9.50001 16C9.50001 15.6022 9.65804 15.2206 9.93935 14.9393C10.2207 14.658 10.6022 14.5 11 14.5H14.5V11C14.5 10.6022 14.658 10.2206 14.9393 9.93934C15.2206 9.65804 15.6022 9.5 16 9.5C16.3978 9.5 16.7794 9.65804 17.0607 9.93934C17.342 10.2206 17.5 10.6022 17.5 11V14.5H21C21.3978 14.5 21.7794 14.658 22.0607 14.9393C22.342 15.2206 22.5 15.6022 22.5 16Z"
                  fill="#BABABA"
                />
              </svg>
            </button>

            <div
              className={`${styles.productCardContainer} ${
                openToLeft ? styles.openLeft : styles.openRight
              }`}
              ref={(el) => {
                cardRefs.current[index] = el;
              }}
              style={{
                transform: `translateY(${cardOffsets[index] || 0}px)`,
                opacity: isOpen ? 1 : 0,
                pointerEvents: activeMarkerIndex === index ? 'auto' : 'none'
              }}
            >
              {productsData?.[index] && (
                <ProductCard
                  product={productsData?.[index]}
                  userSession={userSession}
                  setBasketCount={null}
                  imageCarousel
                  isNested={true}
                />
              )}
            </div>
          </div>
        );
      })}
      <div className={styles.viewProductsContainer}>
        <ViewProducts openOutfitsPopup={openOutfitsPopup} />
      </div>
    </div>
  );
};

export default OutfitCard;
